import { useState, useCallback } from 'react';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import {
  BLOCK_ACTOR_PRACTITIONER,
  BLOCK_PERIOD_DAYS,
  BLOCK_PERIOD_WEEKS,
  BLOCK_WEEK_DAYS,
  type BlockActor,
  type BlockActorKind,
  type BlockAvailabilityRecord,
  type BlockPractitioner,
  type BlockRecurring,
  type BlockRecurringPeriod,
  type BlockResourceAvailabilityRecord,
  type BlockSlotRequest,
  type BlockTimeData,
  type BlockWeekDay,
} from '@/types/block-availability-api';

/**
 * Local form state for BlockAvailabilityPanel — Flutter parity with
 * `_BlockAvailabilityWidgetState.blockAvailability` field
 * (`block_availability_widget.dart:61, 73-80`).
 *
 * Time representation: PR 3 stores start/end as HH:MM 24h strings
 * (the value from `<input type="time">`). Flutter's `TimeData`
 * (12h hour + minute + am/pm format) is recreated only at the wire
 * boundary by `toWireRequest()` below — the React form doesn't need
 * to round-trip through 12h to display correctly, and HTML time
 * inputs return 24h directly.
 *
 * Recurring is still scaffolded for PR 4.
 */
export interface BlockAvailabilityFormState {
  /** The date being blocked. Defaults to today. */
  blockDate: Date;
  /**
   * WHICH kind of actor this panel is blocking — practitioner or device
   * (PROD-2454). Decided by the entry point (`Create ▸ Block Availability` on
   * the Practitioners tab / `Create ▸ Block a device` on the Devices tab) and
   * fixed for the life of the form, so it is seeded by `createInitialState`
   * and deliberately survives every reset below: clearing the selected actor
   * must not silently turn a device panel into a practitioner one.
   */
  actorKind: BlockActorKind;
  /**
   * The selected actor (single — Flutter is NOT multi-select, and one actor
   * is the whole point of the `BlockActor` one-slot model).
   */
  actor: BlockActor | null;
  /** Selected shift key from the SHIFTS dropdown. Used as a UI-only
   *  index; doesn't ship on the wire. The shift's hours bound the
   *  time pickers' `min`/`max`. */
  shiftKey: string | null;
  /** 24h HH:MM string from `<input type="time">`. Null = unset. */
  startTime: string | null;
  /** 24h HH:MM string from `<input type="time">`. Null = unset. */
  endTime: string | null;
  /** Free-text reason — wire field `comment` (Flutter sources this
   *  from the local `note` field, see `block-availability-api.ts`). */
  comment: string;
  /** Recurring rule (PR 4). */
  recurring: BlockRecurring | null;
}

/**
 * A fresh form. Both seeds are read ONCE, at construction — see the invariant
 * on `useBlockAvailabilityForm` — so the panel must be remounted per open for
 * either to apply, which is what its `key` at the call site does (PROD-2515).
 *
 * `initialDate` defaults to today only because a caller that has no calendar
 * behind it has nothing better to offer. `FrameScheduling` always has one and
 * always passes it: a Block rail that opens on today while the calendar behind
 * it shows 1 October is asking the operator to block a day they are not looking
 * at, and the Book-a-device rail beside it has never done that.
 */
export function createInitialState(
  actorKind: BlockActorKind = BLOCK_ACTOR_PRACTITIONER,
  initialDate?: Date,
): BlockAvailabilityFormState {
  return {
    blockDate: startOfDay(initialDate ?? new Date()),
    actorKind,
    actor: null,
    shiftKey: null,
    startTime: null,
    endTime: null,
    comment: '',
    recurring: null,
  };
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/**
 * Hook for the panel. Exposes the state + scoped setters; the
 * setters mirror Flutter's per-action mutations in
 * `_BlockAvailabilityWidgetState`:
 *
 * - `setBlockDate(date)` — also clears the actor + start/end (Flutter
 *   `searchDoctor` resets all three on date change, `:89-96`).
 * - `selectActor(a)` — sets the actor only.
 * - `clearActor()` — full reset to a fresh struct keeping the current
 *   date AND the actor kind (Flutter `:446-454`).
 * - `setStartTime` / `setEndTime` / `setComment` — direct setters
 *   for the break-form fields.
 *
 * `actorKind` and `initialDate` are CONSTRUCTOR arguments, not setters: the
 * panel is opened for one kind of actor, on one date, and stays that way.
 * Making the kind settable would invite a mid-form switch that has to
 * invalidate the actor, the shift list and both times — i.e. exactly
 * `clearActor` plus a new panel, which is what the entry points already give us
 * for free (PROD-2454).
 *
 * PROD-2515 — WHAT THAT INVARIANT COSTS IF THE PANEL IS NOT REMOUNTED, because
 * it shipped broken for exactly this reason. `BlockAvailabilityPanel` lives
 * inside `FrameScheduling` and stays mounted for the whole session, so this
 * lazy initialiser ran ONCE, before any entry point had run, with the default
 * `practitioner`. Choosing "Block a device" then set the prop and nothing read
 * it again: the actor was the machine, the kind was `practitioner`,
 * `useActorShifts` took the presence branch, and
 * `GET /api/Doctor/Presence?practitionerId=<a device id>` answered 500 twice
 * while `ResourceAvailability` was never called. SHIFTS read "No Slots
 * Available" and a device could not be blocked at all. The date seed was stale
 * for the same reason — the rail opened on today while the calendar behind it
 * was on 1 October.
 *
 * The fix is NOT a setter here (that is the mid-flow switch this design refuses
 * on purpose); it is that the caller remounts the panel per open, so the
 * constructor actually runs when the entry point says it does. Every reset path
 * below still carries `prev.actorKind` rather than the argument, unchanged —
 * a reset empties the form, it does not repurpose the panel.
 */
export function useBlockAvailabilityForm(
  actorKind: BlockActorKind = BLOCK_ACTOR_PRACTITIONER,
  initialDate?: Date,
) {
  const [state, setState] = useState<BlockAvailabilityFormState>(() =>
    createInitialState(actorKind, initialDate),
  );

  const setBlockDate = useCallback((date: Date) => {
    setState((prev) => ({
      ...prev,
      blockDate: startOfDay(date),
      actor: null,
      shiftKey: null,
      startTime: null,
      endTime: null,
    }));
  }, []);

  const setShiftKey = useCallback((key: string | null) => {
    setState((prev) => ({ ...prev, shiftKey: key }));
  }, []);

  const selectActor = useCallback((a: BlockActor) => {
    setState((prev) => ({ ...prev, actor: a }));
  }, []);

  const clearActor = useCallback(() => {
    setState((prev) => ({
      // `prev.actorKind`, not the hook argument: the reset must land on the
      // kind the form is actually running as, even if a caller ever re-renders
      // with a different default.
      ...createInitialState(prev.actorKind),
      blockDate: prev.blockDate,
    }));
  }, []);

  const setStartTime = useCallback((time: string | null) => {
    setState((prev) => ({ ...prev, startTime: time }));
  }, []);

  const setEndTime = useCallback((time: string | null) => {
    setState((prev) => ({ ...prev, endTime: time }));
  }, []);

  const setComment = useCallback((comment: string) => {
    setState((prev) => ({ ...prev, comment }));
  }, []);

  /**
   * Toggle recurring on/off. Off → recurring=null. On → seed with
   * the Flutter default: `period="Days"`, `repeatEvery=1`, no end
   * date set. When period later flips to "Weeks", the picker
   * auto-includes the block date's weekday in `days` (Flutter
   * `recurring_widget.dart:60-78`).
   */
  const setRecurringEnabled = useCallback((enabled: boolean) => {
    setState((prev) => ({
      ...prev,
      recurring: enabled
        ? prev.recurring ?? {
            id: null,
            startDate: null,
            endDate: null,
            repeatEvery: 1,
            period: BLOCK_PERIOD_DAYS,
            days: [],
          }
        : null,
    }));
  }, []);

  /** Patch fields on `state.recurring` — no-op when recurring is null. */
  const setRecurring = useCallback((patch: Partial<BlockRecurring>) => {
    setState((prev) => {
      if (!prev.recurring) return prev;
      return { ...prev, recurring: { ...prev.recurring, ...patch } };
    });
  }, []);

  /**
   * Toggle a weekday in `state.recurring.days`. Only meaningful when
   * `period === "Weeks"`. Mirrors Flutter's chip-toggle behavior in
   * `recurring_widget.dart:186-213`.
   */
  const toggleRecurringDay = useCallback((day: BlockWeekDay) => {
    setState((prev) => {
      if (!prev.recurring) return prev;
      const has = prev.recurring.days.includes(day);
      const next = has
        ? prev.recurring.days.filter((d) => d !== day)
        : [...prev.recurring.days, day];
      // Preserve canonical Mon-Sun order so the wire reads stable.
      next.sort((a, b) => BLOCK_WEEK_DAYS.indexOf(a as BlockWeekDay) - BLOCK_WEEK_DAYS.indexOf(b as BlockWeekDay));
      return { ...prev, recurring: { ...prev.recurring, days: next } };
    });
  }, []);

  /**
   * Apply Flutter's "auto-seed block weekday when period flips to
   * Weeks" rule (`recurring_widget.dart:60-78`). Idempotent: only
   * seeds when `days` is currently empty.
   */
  const setRecurringPeriod = useCallback(
    (period: BlockRecurringPeriod) => {
      setState((prev) => {
        if (!prev.recurring) return prev;
        const seedDay =
          period === BLOCK_PERIOD_WEEKS && prev.recurring.days.length === 0
            ? [weekdayName(prev.blockDate)]
            : prev.recurring.days;
        return {
          ...prev,
          recurring: { ...prev.recurring, period, days: seedDay },
        };
      });
    },
    [],
  );

  const reset = useCallback(() => {
    // Keeps the actor KIND for the same reason `clearActor` does — a reset
    // empties the form, it does not repurpose the panel.
    setState((prev) => createInitialState(prev.actorKind));
  }, []);

  /**
   * Hydrate the form state from the record returned by the block-hydration
   * route the panel chose — `GET /api/Appointment/BlockAppointment?id={id}` for
   * a practitioner, `GET /api/Appointment/BlockResourceAppointment?id={id}` for
   * a device (PROD-2512) — edit mode (PR 5).
   *
   * ONE mapping for both, because the two records are one record: the device
   * response DERIVES from the provider one server-side and adds a single field,
   * so everything below except the actor is read from the same key in the same
   * place. Mirroring the route did not mean mirroring this.
   *
   * Mirrors Flutter `block_availability_widget.dart:82-86` where
   * `FetchBlockAvailabilityEvent` lands in
   * `EnableSaveBlockAvailabilityState` which assigns the loaded
   * record onto `blockAvailability`. The mapping here translates
   * the record's wire shape into the form-state shape (12h
   * BlockTimeData → 24h HH:MM, ISO blockDate → Date, etc.).
   */
  const hydrateFromRecord = useCallback(
    (record: BlockAvailabilityRecord | BlockResourceAvailabilityRecord) => {
      setState((prev) => ({
        blockDate: record.blockDate ? parseLocalDate(record.blockDate) : startOfDay(new Date()),
        // The record still does not name its own kind, and it is not asked to:
        // the caller knows (a device block tile can only be clicked on the
        // Devices tab, PROD-2454) and that same knowledge is what picked the
        // route this record came back from. Reading the kind off the payload here
        // would be a second source of truth for a question already answered, and
        // the two could disagree for a render.
        actorKind: prev.actorKind,
        // The actor, from whichever slot the route it came from fills.
        //
        // A DEVICE arrives as a `"Device"` participant (PROD-2512) — the device
        // route never fills `practitioner`, because that field is typed
        // `ProviderResponse` server-side and resolving a device id through the
        // practitioner registry is the 500 this ticket closed.
        //
        // A PRACTITIONER arrives in `practitioner`, which is narrowly TYPED as
        // `BlockPractitioner` ({ id, name, photo?, speciality? }) but ships the
        // wider Flutter `ProviderData` shape — including `firstName`/`lastName`
        // and a NESTED `speciality: { code: {...} }` object. Stuffing the raw
        // record into state crashed the picker (`Objects are not valid as a React
        // child` when rendering the speciality string). Normalize at this
        // boundary so the rest of the form-state contract holds.
        actor: deviceParticipantActor(record) ?? normalizeRecordActor(record.practitioner),
        shiftKey: null, // No shift selection on edit — the record's times override.
        startTime: timeDataToHHMM(record.startTime),
        endTime: timeDataToHHMM(record.endTime),
        // Wire uses `comment` for the user-typed reason (see the inline
        // comment on `BlockSlotRequest.comment`); response surfaces both
        // `note` and `comment`. Prefer `note` (Flutter source-of-truth)
        // and fall back to `comment` for defensive reads.
        comment: record.note ?? record.comment ?? '',
        recurring: record.recurring,
      }));
    },
    [],
  );

  return {
    state,
    setBlockDate,
    selectActor,
    clearActor,
    setShiftKey,
    setStartTime,
    setEndTime,
    setComment,
    setRecurringEnabled,
    setRecurring,
    setRecurringPeriod,
    toggleRecurringDay,
    hydrateFromRecord,
    reset,
  };
}

/**
 * Parse an ISO-ish local datetime string (no-Z, e.g. "2026-05-18T13:00:00")
 * into a Date without timezone shift. Uses the date components directly
 * — `new Date(iso)` would interpret a no-Z string as UTC and shift the
 * day for east-of-UTC users.
 */
function parseLocalDate(iso: string): Date {
  // Strip any trailing 'Z' the backend might add inconsistently.
  const clean = iso.replace(/Z$/, '');
  const [datePart] = clean.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * Convert Flutter `TimeData` (12h hour + minute + 'am'/'pm') to a
 * 24h `HH:MM` string. Returns null when any field is missing.
 *
 * Flutter `time12to24Format` (`block_availability_model.dart:108-123`):
 *   PM: hour + 12 unless hour == 12; AM: hour stays except 12 → 0.
 */
function timeDataToHHMM(t: BlockTimeData | null): string | null {
  if (!t || t.hour == null || t.minute == null) return null;
  const fmt = (t.format ?? '').toLowerCase();
  let h = t.hour;
  if (fmt === 'pm' && h !== 12) h += 12;
  if (fmt === 'am' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
}

/** Map a Date to its weekday name (`"Monday"` etc) matching the
 *  `BLOCK_WEEK_DAYS` order. */
function weekdayName(d: Date): BlockWeekDay {
  // JS Date.getDay() returns 0=Sunday..6=Saturday; BLOCK_WEEK_DAYS is
  // Mon-first. Map index accordingly.
  const jsDay = d.getDay();
  const monFirstIndex = (jsDay + 6) % 7;
  return BLOCK_WEEK_DAYS[monFirstIndex];
}

/**
 * The DEVICE actor on a record from `/api/Appointment/BlockResourceAppointment`
 * (PROD-2512), or null when this record is not one — which is how the caller
 * above uses it: the device slot first, the practitioner slot otherwise.
 *
 * The device rides in a `"Device"` participant rather than the inherited
 * `practitioner` slot, because that slot is typed `ProviderResponse`
 * server-side; resolving a device id through the practitioner registry is
 * exactly the 500 this ticket closed. The shape is the one a device tile
 * already arrives with from `ResourceList` (PROD-2382) and over SignalR
 * (PROD-2510), so no new mapping is invented here.
 *
 * `referenceType` is checked rather than assumed. The route guarantees a Device
 * participant — the server refuses a non-device block on it outright — but this
 * is the boundary where a wrong-shaped payload would otherwise become a
 * confidently-wrong actor card, and a null actor renders as an empty picker the
 * operator can see rather than a machine named after a practitioner.
 *
 * `display` is null for a machine archived out of the registry; the picker
 * prefers the registry row for the same id anyway and only falls back to this
 * name, so an empty string here would be worse than the null.
 */
function deviceParticipantActor(
  record: BlockAvailabilityRecord | BlockResourceAvailabilityRecord,
): BlockActor | null {
  if (!('participant' in record)) return null;
  const device = record.participant?.find(
    (p) => p?.referenceType?.toLowerCase() === 'device' && !!p.reference,
  );
  if (!device?.reference) return null;
  return {
    id: device.reference,
    name: device.display ?? 'Unknown',
    photo: null,
    subtitle: null,
  };
}

/**
 * Normalize the actor shape coming back from
 * `/api/Appointment/BlockAppointment` into the narrow `BlockActor` the
 * pickers expect.
 *
 * The backend ships the wider Flutter `ProviderData` shape on this
 * endpoint:
 *   - `name` may be missing; reconstruct from `firstName + lastName`.
 *   - `speciality` is a nested object `{ specialitySkillId, code: { display, displayName } }`,
 *     NOT a string. Convert via `specialityDisplay` (same helper used
 *     by the appointment-panel picker).
 *   - `photo` may also surface as `practitionerImage`.
 *
 * Without this normalization, the React picker tried to render the
 * nested speciality object as a `<p>` child and crashed the tree
 * with "Objects are not valid as a React child".
 *
 * Since PROD-2512 a device no longer reaches here at all: it has its own route,
 * which names it as a `"Device"` participant and leaves `practitioner` null, so
 * `deviceParticipantActor` above answers first. The device fallback that used
 * to live in this function is gone with the practitioner-shaped slot it worked
 * around — reading a machine out of a field typed for practitioners is the
 * shape of the bug, not a harmless leftover.
 */
function normalizeRecordActor(
  raw: BlockAvailabilityRecord['practitioner'],
): BlockActor | null {
  if (!raw) return null;
  const wide = raw as BlockPractitioner & {
    firstName?: string | null;
    lastName?: string | null;
    practitionerImage?: string | null;
    speciality?: unknown;
  };
  const composedName =
    (wide.name && wide.name.trim()) ||
    `${wide.firstName ?? ''} ${wide.lastName ?? ''}`.trim() ||
    'Unknown';
  return {
    id: wide.id,
    name: composedName,
    photo: wide.photo ?? wide.practitionerImage ?? null,
    // specialityDisplay returns string | null — handles both the
    // narrow string-already shape and the nested-object wire shape.
    subtitle:
      typeof wide.speciality === 'string'
        ? wide.speciality
        : specialityDisplay(
            wide.speciality as Parameters<typeof specialityDisplay>[0],
          ) ?? null,
  };
}

/**
 * Pad to 2 digits with leading zero. `5` → `"05"`.
 */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Compose a no-Z ISO local datetime string from a `blockDate` Date
 * and an `HH:MM` 24h time. Used to build the wire request body.
 *
 * Format: `YYYY-MM-DDTHH:mm:ss.sss` — verified via dev curl
 * 2026-05-18. The .NET model binder REJECTS the `Z` suffix on
 * `/api/Scheduling/BlockSlot` (and most other PM scheduling
 * endpoints). Built from local-clock components so no inadvertent
 * UTC shift kicks in for east-of-UTC users (Asia/Dubai = +04).
 */
export function composeWireDateTime(blockDate: Date, hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const y = blockDate.getFullYear();
  const m = pad2(blockDate.getMonth() + 1);
  const d = pad2(blockDate.getDate());
  return `${y}-${m}-${d}T${pad2(Number(hStr))}:${pad2(Number(mStr))}:00.000`;
}

/**
 * Compose the wire `recurring` object from the form state. Returns
 * null when recurring is disabled or incomplete.
 *
 * Mirrors Flutter `block_availability_widget.dart:222-237`:
 *   - `startDate` = blockDate (y/m/d) + START time (h/m).
 *   - `endDate`   = recurring.endDate (y/m/d, user-picked) + END time (h/m).
 *
 * If `recurringEndDate` is unset, returns null — same as Flutter
 * where the Save guard requires end date.
 */
export function composeWireRecurring(
  state: BlockAvailabilityFormState,
): BlockRecurring | null {
  const r = state.recurring;
  if (!r) return null;
  if (!r.repeatEvery || r.repeatEvery < 1) return null;
  if (!r.period) return null;
  if (!r.endDate) return null;
  if (!state.startTime || !state.endTime) return null;
  // r.endDate ships in as ISO from the form state (we set it that way
  // in setRecurring); rebuild it with the End time so the server has
  // both the date AND time for the recurring window's last day.
  const endDateOnly = new Date(r.endDate);
  return {
    id: r.id,
    startDate: composeWireDateTime(state.blockDate, state.startTime),
    endDate: composeWireDateTime(endDateOnly, state.endTime),
    repeatEvery: r.repeatEvery,
    period: r.period,
    // Weeks: ship the selected days. Days/Months: ship empty array
    // (Flutter `Recurring.toJson()` ships `[]` when `days` is null).
    days: r.period === BLOCK_PERIOD_WEEKS ? r.days : [],
  };
}

/**
 * Marshal the form state into a `BlockSlotRequest` ready for
 * `useSaveBlockSlot`. Returns `null` if the state is incomplete —
 * callers should validate first via `validateBlockForm`.
 */
export function toWireRequest(
  state: BlockAvailabilityFormState,
  practiceId: string,
): BlockSlotRequest | null {
  if (!state.actor?.id) return null;
  if (!state.startTime || !state.endTime) return null;
  if (!state.comment.trim()) return null;
  // Recurring composition fails validation independently — when
  // recurring is enabled but incomplete, the whole request is invalid.
  // When recurring is disabled (state.recurring === null),
  // composeWireRecurring returns null which is the expected wire value.
  let recurring: BlockRecurring | null = null;
  if (state.recurring) {
    recurring = composeWireRecurring(state);
    if (!recurring) return null;
  }
  return {
    // `actorId`, for EITHER kind. The endpoint has never taken a
    // `practitionerId` — a device block is the same six keys with a device
    // GUID in this one (PROD-2454). `state.actorKind` stays client-side.
    actorId: state.actor.id,
    start: composeWireDateTime(state.blockDate, state.startTime),
    end: composeWireDateTime(state.blockDate, state.endTime),
    comment: state.comment.trim(),
    practiceId,
    recurring,
  };
}
